import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, LogOut, Check, X, Trash2, Phone, Mail, MapPin, Plane, Calendar, RefreshCw, DollarSign, Users, KeyRound, ClipboardList, UserPlus } from "lucide-react";
import { BRAND } from "../mock";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "ftz_admin_token";

const STATUS = {
  pending: { label: "Ausstehend", cls: "bg-[#FFF4E5] text-[#B26A00]" },
  confirmed: { label: "Bestätigt", cls: "bg-[#E7F8EE] text-[#1B7F45]" },
  cancelled: { label: "Storniert", cls: "bg-[#FDECEC] text-[#C0392B]" },
};

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [pricing, setPricing] = useState([]);
  const [extras, setExtras] = useState({ night: { enabled: true, startHour: 0, endHour: 5, percent: 15 }, childSeats: [] });
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(null);

  const authHeaders = useCallback(() => ({ "Content-Type": "application/json", Authorization: `Bearer ${token}` }), [token]);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setBookings([]); setDrivers([]); setPricing([]);
  }, []);

  const api = useCallback(async (path, opts = {}) => {
    const res = await fetch(`${API}${path}`, { ...opts, headers: { ...authHeaders(), ...(opts.headers || {}) } });
    if (res.status === 401) { logout(); toast.error("Sitzung abgelaufen."); throw new Error("401"); }
    return res;
  }, [authHeaders, logout]);

  const loadAll = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [bRes, dRes, pRes, sRes] = await Promise.all([
        api("/api/admin/bookings"),
        api("/api/admin/drivers"),
        fetch(`${API}/api/pricing`),
        fetch(`${API}/api/settings`),
      ]);
      setBookings(await bRes.json());
      setDrivers(await dRes.json());
      setPricing(await pRes.json());
      setExtras(await sRes.json());
    } catch (e) {
      if (e.message !== "401") toast.error("Fehler beim Laden.");
    } finally {
      setLoading(false);
    }
  }, [token, api]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const res = await fetch(`${API}/api/auth/login`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email, password }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Login fehlgeschlagen");
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      toast.success("Willkommen zurück!");
    } catch (err) {
      toast.error(typeof err.message === "string" ? err.message : "Login fehlgeschlagen");
    } finally {
      setLoggingIn(false);
    }
  };

  const setStatus = async (id, status) => {
    setBusy(id + status);
    try {
      const res = await api(`/api/admin/bookings/${id}/status`, { method: "PATCH", body: JSON.stringify({ status }) });
      if (!res.ok) throw new Error();
      setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, status } : b)));
      toast.success(status === "confirmed" ? "Bestätigt – E-Mail an Kunde gesendet." : "Storniert – E-Mail an Kunde gesendet.");
    } catch (e) { toast.error("Aktion fehlgeschlagen."); } finally { setBusy(null); }
  };

  const removeBooking = async (id) => {
    if (!window.confirm("Diese Buchung wirklich löschen?")) return;
    setBusy(id + "del");
    try {
      const res = await api(`/api/admin/bookings/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error();
      setBookings((prev) => prev.filter((b) => b.id !== id));
      toast.success("Buchung gelöscht.");
    } catch (e) { toast.error("Löschen fehlgeschlagen."); } finally { setBusy(null); }
  };

  const assign = async (id, driver_id) => {
    if (!driver_id) return;
    setBusy(id + "assign");
    try {
      const res = await api(`/api/admin/bookings/${id}/assign`, { method: "PATCH", body: JSON.stringify({ driver_id }) });
      const data = await res.json();
      if (!res.ok) throw new Error();
      setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, driver: data.driver } : b)));
      toast.success(`Fahrer zugewiesen – E-Mail an ${data.driver.name} gesendet.`);
    } catch (e) { toast.error("Zuweisung fehlgeschlagen."); } finally { setBusy(null); }
  };

  // ---------- Login screen ----------
  if (!token) {
    return (
      <div className="min-h-screen bg-[#0B1533] flex items-center justify-center px-4">
        <form onSubmit={login} className="w-full max-w-sm bg-white rounded-3xl p-8 elev-lg" data-testid="admin-login-form">
          <div className="flex justify-center mb-6"><img src={BRAND.logo} alt="Logo" className="h-12 bg-white rounded-xl px-3 py-1" /></div>
          <h1 className="font-heading font-bold text-2xl text-[#0B1533] text-center">Admin-Anmeldung</h1>
          <p className="font-body text-sm text-[#5A607A] text-center mt-1 mb-6">Buchungen verwalten</p>
          <label className="font-body text-xs text-[#5A607A] mb-1.5 block">E-Mail</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="admin-email" className="w-full bg-white border border-[#E9E5DE] rounded-2xl px-4 py-3.5 font-body text-sm mb-4 focus:border-[#2B4BFF] focus:outline-none" />
          <label className="font-body text-xs text-[#5A607A] mb-1.5 block">Passwort</label>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="admin-password" className="w-full bg-white border border-[#E9E5DE] rounded-2xl px-4 py-3.5 font-body text-sm mb-6 focus:border-[#2B4BFF] focus:outline-none" />
          <button type="submit" disabled={loggingIn} data-testid="admin-login-btn" className="w-full inline-flex items-center justify-center gap-2 bg-[#2B4BFF] text-white font-body font-semibold rounded-full py-3.5 hover:bg-[#1E38D8] transition-colors disabled:opacity-50">
            {loggingIn ? <Loader2 size={18} className="animate-spin" /> : "Anmelden"}
          </button>
        </form>
      </div>
    );
  }

  const counts = { all: bookings.length, pending: bookings.filter((b) => b.status === "pending").length, confirmed: bookings.filter((b) => b.status === "confirmed").length, cancelled: bookings.filter((b) => b.status === "cancelled").length };
  const filtered = filter === "all" ? bookings : bookings.filter((b) => b.status === filter);

  const TABS = [
    { id: "bookings", label: "Buchungen", icon: ClipboardList },
    { id: "calendar", label: "Kalender", icon: Calendar },
    { id: "pricing", label: "Preise", icon: DollarSign },
    { id: "drivers", label: "Fahrer", icon: Users },
    { id: "settings", label: "Passwort", icon: KeyRound },
  ];

  return (
    <div className="min-h-screen bg-[#F5F2ED]">
      <header className="bg-[#0B1533] text-white sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={BRAND.logo} alt="Logo" className="h-9 bg-white rounded-lg px-2 py-1" />
            <span className="font-heading font-semibold hidden sm:block">Admin</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={loadAll} className="inline-flex items-center gap-2 border border-white/20 rounded-full px-4 py-2 text-sm hover:bg-white/10 transition-colors" data-testid="admin-refresh"><RefreshCw size={15} className={loading ? "animate-spin" : ""} /> <span className="hidden sm:inline">Aktualisieren</span></button>
            <button onClick={logout} className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-2 text-sm hover:bg-white/20 transition-colors" data-testid="admin-logout"><LogOut size={15} /> <span className="hidden sm:inline">Abmelden</span></button>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`} className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-body font-semibold border-b-2 whitespace-nowrap transition-colors ${tab === t.id ? "border-white text-white" : "border-transparent text-white/50 hover:text-white/80"}`}>
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {tab === "bookings" && (
          <BookingsTab {...{ counts, filter, setFilter, filtered, loading, bookings, drivers, busy, setStatus, removeBooking, assign, STATUS }} />
        )}
        {tab === "calendar" && <CalendarTab bookings={bookings} STATUS={STATUS} />}
        {tab === "pricing" && <PricingTab pricing={pricing} setPricing={setPricing} extras={extras} setExtras={setExtras} api={api} />}
        {tab === "drivers" && <DriversTab drivers={drivers} setDrivers={setDrivers} api={api} />}
        {tab === "settings" && <SettingsTab api={api} onToken={(tk) => { localStorage.setItem(TOKEN_KEY, tk); setToken(tk); }} />}
      </div>
    </div>
  );
};

const Field = ({ label, ...rest }) => (
  <div><label className="font-body text-xs text-[#5A607A] mb-1 block">{label}</label><input {...rest} className="w-full bg-white border border-[#E9E5DE] rounded-xl px-3 py-2.5 font-body text-sm focus:border-[#2B4BFF] focus:outline-none" /></div>
);

const BookingsTab = ({ counts, filter, setFilter, filtered, loading, bookings, drivers, busy, setStatus, removeBooking, assign, STATUS }) => (
  <>
    <div className="flex flex-wrap gap-2 mb-6">
      {["all", "pending", "confirmed", "cancelled"].map((f) => (
        <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`} className={`font-body text-sm rounded-full px-4 py-2 border transition-colors ${filter === f ? "bg-[#0B1533] text-white border-[#0B1533]" : "bg-white text-[#5A607A] border-[#E9E5DE] hover:border-[#0B1533]"}`}>
          {f === "all" ? "Alle" : STATUS[f].label} ({counts[f]})
        </button>
      ))}
    </div>
    {loading && bookings.length === 0 ? (
      <p className="font-body text-[#5A607A] flex items-center gap-2"><Loader2 size={18} className="animate-spin" /> Lädt …</p>
    ) : filtered.length === 0 ? (
      <div className="bg-white rounded-2xl border border-[#E9E5DE] p-12 text-center font-body text-[#5A607A]">Keine Buchungen in dieser Kategorie.</div>
    ) : (
      <div className="grid gap-4">
        {filtered.map((b) => (
          <div key={b.id} className="bg-white rounded-2xl border border-[#E9E5DE] p-5 sm:p-6" data-testid={`booking-${b.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
              <div>
                <div className="flex items-center gap-3 flex-wrap">
                  <h3 className="font-heading font-semibold text-lg text-[#0B1533]">{b.name}</h3>
                  <span className={`font-body text-xs font-semibold rounded-full px-3 py-1 ${STATUS[b.status]?.cls || ""}`}>{STATUS[b.status]?.label || b.status}</span>
                  {b.payment_status && (
                    <span data-testid={`payment-badge-${b.id}`} className={`font-body text-xs font-semibold rounded-full px-3 py-1 ${b.payment_status === "PAID" ? "bg-[#E7F8EE] text-[#1B7F45]" : b.payment_status === "PENDING" ? "bg-[#FFF6E5] text-[#B77900]" : "bg-[#FDECEC] text-[#C0392B]"}`}>
                      {b.payment_status === "PAID" ? "💳 Bezahlt" : b.payment_status === "PENDING" ? "⏳ Zahlung offen" : `Zahlung: ${b.payment_status}`}
                    </span>
                  )}
                  {b.price && <span className="font-heading font-bold text-[#0B1533]">CHF {b.price}.–</span>}
                </div>
                <p className="font-body text-xs text-[#9CA0B0] mt-1">Nr. {b.ref || b.id} · {b.created_at ? new Date(b.created_at).toLocaleString("de-CH") : ""}</p>
              </div>
              <div className="flex gap-2">
                <button disabled={b.status === "confirmed" || busy} onClick={() => setStatus(b.id, "confirmed")} data-testid={`confirm-${b.id}`} className="inline-flex items-center gap-1.5 bg-[#25D366] text-white font-body text-sm font-semibold rounded-full px-4 py-2 hover:opacity-90 transition-opacity disabled:opacity-40">{busy === b.id + "confirmed" ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Bestätigen</button>
                <button disabled={b.status === "cancelled" || busy} onClick={() => setStatus(b.id, "cancelled")} data-testid={`cancel-${b.id}`} className="inline-flex items-center gap-1.5 bg-[#FDECEC] text-[#C0392B] font-body text-sm font-semibold rounded-full px-4 py-2 hover:bg-[#f9dede] transition-colors disabled:opacity-40">{busy === b.id + "cancelled" ? <Loader2 size={15} className="animate-spin" /> : <X size={15} />} Stornieren</button>
                <button disabled={busy} onClick={() => removeBooking(b.id)} className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#F5F2ED] text-[#5A607A] hover:text-[#C0392B] transition-colors" title="Löschen"><Trash2 size={15} /></button>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2 font-body text-sm text-[#0B1533]">
              <p className="flex items-center gap-2"><Plane size={15} className="text-[#2B4BFF]" /> {b.pickup}</p>
              <p className="flex items-center gap-2"><MapPin size={15} className="text-[#2B4BFF]" /> {b.dropoff}</p>
              <p className="flex items-center gap-2"><Calendar size={15} className="text-[#5A607A]" /> {b.date} · {b.time}</p>
              <p className="flex items-center gap-2 text-[#5A607A]">{b.vehicle} · {b.passengers}</p>
              <p className="flex items-center gap-2"><Phone size={15} className="text-[#5A607A]" /> <a href={`tel:${b.phone}`} className="hover:text-[#2B4BFF]">{b.phone}</a></p>
              <p className="flex items-center gap-2"><Mail size={15} className="text-[#5A607A]" /> <a href={`mailto:${b.email}`} className="hover:text-[#2B4BFF]">{b.email}</a></p>
              {b.flight && <p className="text-[#5A607A]">Flug: {b.flight}</p>}
              {b.notes && <p className="sm:col-span-2 text-[#5A607A]">Notiz: {b.notes}</p>}
            </div>
            <div className="mt-4 pt-4 border-t border-[#F0EDE7] flex items-center gap-3 flex-wrap">
              <span className="font-body text-xs text-[#5A607A] inline-flex items-center gap-1.5"><UserPlus size={14} /> Fahrer:</span>
              <select value={b.driver?.id || ""} onChange={(e) => assign(b.id, e.target.value)} data-testid={`assign-${b.id}`} className="bg-white border border-[#E9E5DE] rounded-full px-4 py-1.5 font-body text-sm focus:border-[#2B4BFF] focus:outline-none">
                <option value="">— Fahrer wählen —</option>
                {drivers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
              {b.driver && <span className="font-body text-xs text-[#1B7F45] bg-[#E7F8EE] rounded-full px-3 py-1">Zugewiesen: {b.driver.name}</span>}
            </div>
          </div>
        ))}
      </div>
    )}
  </>
);

const CalendarTab = ({ bookings, STATUS }) => {
  const byDate = {};
  bookings.forEach((b) => { (byDate[b.date] = byDate[b.date] || []).push(b); });
  const dates = Object.keys(byDate).sort();
  if (dates.length === 0) return <div className="bg-white rounded-2xl border border-[#E9E5DE] p-12 text-center font-body text-[#5A607A]">Noch keine geplanten Fahrten.</div>;
  return (
    <div className="grid gap-4">
      {dates.map((d) => (
        <div key={d} className="bg-white rounded-2xl border border-[#E9E5DE] p-5" data-testid={`cal-${d}`}>
          <h3 className="font-heading font-semibold text-[#0B1533] mb-3 flex items-center gap-2"><Calendar size={16} className="text-[#2B4BFF]" /> {new Date(d).toLocaleDateString("de-CH", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}</h3>
          <div className="space-y-2">
            {[...byDate[d]].sort((a, b) => (a.time || "").localeCompare(b.time || "")).map((b) => (
              <div key={b.id} className="flex items-center gap-3 flex-wrap text-sm font-body border-l-2 border-[#2B4BFF] pl-3 py-1">
                <span className="font-semibold text-[#0B1533] w-12">{b.time}</span>
                <span className="text-[#0B1533]">{b.dropoff}</span>
                <span className="text-[#5A607A]">· {b.name}</span>
                <span className={`text-xs rounded-full px-2 py-0.5 ${STATUS[b.status]?.cls || ""}`}>{STATUS[b.status]?.label}</span>
                {b.driver && <span className="text-xs text-[#5A607A]">🚗 {b.driver.name}</span>}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const PricingTab = ({ pricing, setPricing, extras, setExtras, api }) => {
  const [rows, setRows] = useState(pricing);
  const [saving, setSaving] = useState(false);
  const [night, setNight] = useState(extras.night || { enabled: true, startHour: 0, endHour: 5, percent: 15 });
  const [seats, setSeats] = useState(extras.childSeats || []);
  const [savingX, setSavingX] = useState(false);
  useEffect(() => { setRows(pricing); }, [pricing]);
  useEffect(() => { setNight(extras.night || { enabled: true, startHour: 0, endHour: 5, percent: 15 }); setSeats(extras.childSeats || []); }, [extras]);
  const upd = (i, key, val) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, [key]: key === "name" ? val : parseFloat(val) || 0 } : row)));
  const save = async () => {
    setSaving(true);
    try {
      const res = await api("/api/admin/pricing", { method: "PUT", body: JSON.stringify({ vehicles: rows }) });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setPricing(data.vehicles);
      toast.success("Preise gespeichert.");
    } catch (e) { toast.error("Speichern fehlgeschlagen."); } finally { setSaving(false); }
  };
  const saveExtras = async () => {
    setSavingX(true);
    try {
      const res = await api("/api/admin/settings", { method: "PUT", body: JSON.stringify({ night, childSeats: seats }) });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setExtras({ night: data.night, childSeats: data.childSeats });
      toast.success("Zuschläge gespeichert.");
    } catch (e) { toast.error("Speichern fehlgeschlagen."); } finally { setSavingX(false); }
  };
  const hours = Array.from({ length: 24 }, (_, i) => i);
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-[#E9E5DE] p-5 sm:p-6" data-testid="pricing-tab">
        <p className="font-body text-sm text-[#5A607A] mb-4">Der <strong>Grundpreis</strong> ist der garantierte Mindestpreis (Startpreis). Der <strong>Preis/km</strong> gilt nur für längere Strecken, sobald er den Grundpreis übersteigt. Änderungen wirken sofort auf der Website.</p>
        <div className="space-y-3">
          {rows.map((v, i) => (
            <div key={v.id} className="grid sm:grid-cols-[2fr_1fr_1fr] gap-3 items-end border-b border-[#F0EDE7] pb-3">
              <Field label="Fahrzeug" value={v.name} onChange={(e) => upd(i, "name", e.target.value)} data-testid={`price-name-${v.id}`} />
              <Field label="Grundpreis (CHF)" type="number" step="1" value={v.base} onChange={(e) => upd(i, "base", e.target.value)} data-testid={`price-base-${v.id}`} />
              <Field label="Preis / km (CHF)" type="number" step="0.1" value={v.perKm} onChange={(e) => upd(i, "perKm", e.target.value)} data-testid={`price-perkm-${v.id}`} />
            </div>
          ))}
        </div>
        <button onClick={save} disabled={saving} data-testid="save-pricing" className="mt-5 inline-flex items-center gap-2 bg-[#2B4BFF] text-white font-body font-semibold rounded-full px-7 py-3 hover:bg-[#1E38D8] transition-colors disabled:opacity-50">{saving ? <Loader2 size={18} className="animate-spin" /> : "Preise speichern"}</button>
      </div>

      <div className="bg-white rounded-2xl border border-[#E9E5DE] p-5 sm:p-6" data-testid="night-tab">
        <h3 className="font-heading font-semibold text-[#0B1533] mb-1">Nachtzuschlag</h3>
        <p className="font-body text-sm text-[#5A607A] mb-4">Prozentualer Aufschlag auf den Fahrpreis für Fahrten im gewählten Zeitfenster (z. B. 10–20 % von 00:00 bis 05:00). Zeit und Prozentsatz frei wählbar.</p>
        <label className="flex items-center gap-2 mb-4 font-body text-sm text-[#0B1533]">
          <input type="checkbox" checked={!!night.enabled} onChange={(e) => setNight({ ...night, enabled: e.target.checked })} data-testid="night-enabled" className="w-4 h-4 accent-[#2B4BFF]" /> Nachtzuschlag aktiv
        </label>
        <div className="grid sm:grid-cols-3 gap-3 items-end">
          <div>
            <label className="font-body text-xs text-[#5A607A] mb-1.5 block uppercase tracking-wider">Von (Stunde)</label>
            <select value={night.startHour} onChange={(e) => setNight({ ...night, startHour: parseInt(e.target.value, 10) })} data-testid="night-start" className="w-full bg-white border border-[#E9E5DE] rounded-2xl px-4 py-3 font-body text-sm focus:border-[#2B4BFF] focus:outline-none">{hours.map((h) => <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>)}</select>
          </div>
          <div>
            <label className="font-body text-xs text-[#5A607A] mb-1.5 block uppercase tracking-wider">Bis (Stunde)</label>
            <select value={night.endHour} onChange={(e) => setNight({ ...night, endHour: parseInt(e.target.value, 10) })} data-testid="night-end" className="w-full bg-white border border-[#E9E5DE] rounded-2xl px-4 py-3 font-body text-sm focus:border-[#2B4BFF] focus:outline-none">{hours.map((h) => <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>)}</select>
          </div>
          <Field label="Aufschlag (%)" type="number" step="1" value={night.percent} onChange={(e) => setNight({ ...night, percent: parseFloat(e.target.value) || 0 })} data-testid="night-percent" />
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[#E9E5DE] p-5 sm:p-6" data-testid="childseat-tab">
        <h3 className="font-heading font-semibold text-[#0B1533] mb-1">Kindersitz-Preise</h3>
        <p className="font-body text-sm text-[#5A607A] mb-4">Preis pro Kindersitz (CHF). 0 = gratis. Kunden wählen die Anzahl im Buchungsschritt.</p>
        <div className="space-y-3">
          {seats.map((c, i) => (
            <div key={c.id} className="grid sm:grid-cols-[2fr_1fr] gap-3 items-end border-b border-[#F0EDE7] pb-3">
              <Field label="Kindersitz" value={c.name} onChange={(e) => setSeats((s) => s.map((x, idx) => idx === i ? { ...x, name: e.target.value } : x))} data-testid={`seat-name-${c.id}`} />
              <Field label="Preis (CHF)" type="number" step="1" value={c.price} onChange={(e) => setSeats((s) => s.map((x, idx) => idx === i ? { ...x, price: parseFloat(e.target.value) || 0 } : x))} data-testid={`seat-price-${c.id}`} />
            </div>
          ))}
        </div>
        <button onClick={saveExtras} disabled={savingX} data-testid="save-extras" className="mt-5 inline-flex items-center gap-2 bg-[#2B4BFF] text-white font-body font-semibold rounded-full px-7 py-3 hover:bg-[#1E38D8] transition-colors disabled:opacity-50">{savingX ? <Loader2 size={18} className="animate-spin" /> : "Nachtzuschlag & Kindersitze speichern"}</button>
      </div>
    </div>
  );
};

const DriversTab = ({ drivers, setDrivers, api }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [adding, setAdding] = useState(false);
  const add = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      const res = await api("/api/admin/drivers", { method: "POST", body: JSON.stringify(form) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      setDrivers((d) => [...d, data]);
      setForm({ name: "", email: "", phone: "" });
      toast.success("Fahrer hinzugefügt.");
    } catch (e) { toast.error("Hinzufügen fehlgeschlagen."); } finally { setAdding(false); }
  };
  const del = async (id) => {
    if (!window.confirm("Fahrer löschen?")) return;
    try {
      const res = await api(`/api/admin/drivers/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error();
      setDrivers((d) => d.filter((x) => x.id !== id));
      toast.success("Fahrer gelöscht.");
    } catch (e) { toast.error("Löschen fehlgeschlagen."); }
  };
  return (
    <div className="grid md:grid-cols-2 gap-6" data-testid="drivers-tab">
      <form onSubmit={add} className="bg-white rounded-2xl border border-[#E9E5DE] p-5 sm:p-6 space-y-3 h-fit">
        <h3 className="font-heading font-semibold text-[#0B1533]">Neuer Fahrer</h3>
        <Field label="Name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="driver-name" />
        <Field label="E-Mail" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="driver-email" />
        <Field label="Telefon" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="driver-phone" />
        <button type="submit" disabled={adding} data-testid="add-driver" className="inline-flex items-center gap-2 bg-[#2B4BFF] text-white font-body font-semibold rounded-full px-6 py-2.5 hover:bg-[#1E38D8] transition-colors disabled:opacity-50">{adding ? <Loader2 size={16} className="animate-spin" /> : "Hinzufügen"}</button>
      </form>
      <div className="space-y-3">
        {drivers.length === 0 ? <div className="bg-white rounded-2xl border border-[#E9E5DE] p-8 text-center font-body text-[#5A607A]">Noch keine Fahrer.</div> : drivers.map((d) => (
          <div key={d.id} className="bg-white rounded-2xl border border-[#E9E5DE] p-4 flex items-center justify-between" data-testid={`driver-${d.id}`}>
            <div><p className="font-heading font-semibold text-[#0B1533]">{d.name}</p><p className="font-body text-xs text-[#5A607A]">{d.email} · {d.phone}</p></div>
            <button onClick={() => del(d.id)} className="w-9 h-9 rounded-full bg-[#F5F2ED] text-[#5A607A] hover:text-[#C0392B] transition-colors flex items-center justify-center"><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

const SettingsTab = ({ api, onToken }) => {
  const [form, setForm] = useState({ current_password: "", new_password: "" });
  const [saving, setSaving] = useState(false);
  const [eform, setEform] = useState({ current_password: "", new_email: "" });
  const [esaving, setEsaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api("/api/admin/change-password", { method: "POST", body: JSON.stringify(form) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      setForm({ current_password: "", new_password: "" });
      toast.success("Passwort geändert.");
    } catch (err) { toast.error(typeof err.message === "string" ? err.message : "Fehlgeschlagen"); } finally { setSaving(false); }
  };
  const submitEmail = async (e) => {
    e.preventDefault();
    setEsaving(true);
    try {
      const res = await api("/api/admin/change-email", { method: "POST", body: JSON.stringify(eform) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      if (data.token && onToken) onToken(data.token);
      setEform({ current_password: "", new_email: "" });
      toast.success("E-Mail geändert. Neue Login-Adresse: " + data.email);
    } catch (err) { toast.error(typeof err.message === "string" ? err.message : "Fehlgeschlagen"); } finally { setEsaving(false); }
  };
  return (
    <div className="grid md:grid-cols-2 gap-6" data-testid="settings-tab">
      <form onSubmit={submitEmail} className="bg-white rounded-2xl border border-[#E9E5DE] p-5 sm:p-6 space-y-3" data-testid="change-email-form">
        <h3 className="font-heading font-semibold text-[#0B1533]">Admin-E-Mail ändern</h3>
        <p className="font-body text-xs text-[#5A607A]">Dies ist auch Ihre Login-Adresse. Zur Bestätigung Ihr aktuelles Passwort eingeben.</p>
        <Field label="Neue E-Mail" type="email" required value={eform.new_email} onChange={(e) => setEform({ ...eform, new_email: e.target.value })} data-testid="new-email" />
        <Field label="Aktuelles Passwort" type="password" required value={eform.current_password} onChange={(e) => setEform({ ...eform, current_password: e.target.value })} data-testid="email-current-password" />
        <button type="submit" disabled={esaving} data-testid="save-email" className="inline-flex items-center gap-2 bg-[#2B4BFF] text-white font-body font-semibold rounded-full px-6 py-2.5 hover:bg-[#1E38D8] transition-colors disabled:opacity-50">{esaving ? <Loader2 size={16} className="animate-spin" /> : "E-Mail ändern"}</button>
      </form>
      <form onSubmit={submit} className="bg-white rounded-2xl border border-[#E9E5DE] p-5 sm:p-6 space-y-3">
        <h3 className="font-heading font-semibold text-[#0B1533]">Passwort ändern</h3>
        <Field label="Aktuelles Passwort" type="password" required value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} data-testid="current-password" />
        <Field label="Neues Passwort (min. 6 Zeichen)" type="password" required value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} data-testid="new-password" />
        <button type="submit" disabled={saving} data-testid="save-password" className="inline-flex items-center gap-2 bg-[#2B4BFF] text-white font-body font-semibold rounded-full px-6 py-2.5 hover:bg-[#1E38D8] transition-colors disabled:opacity-50">{saving ? <Loader2 size={16} className="animate-spin" /> : "Passwort ändern"}</button>
      </form>
    </div>
  );
};

export default Admin;
